/**
 * GuidedWalkthrough — the in-app half of training. Spotlights the real
 * element on the real screen, step by step, and records progress as you go.
 *
 * Launched from the Training Center with ?walkthrough={trackId}; it lives in
 * the admin shell so it can follow you across routes.
 */
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, GraduationCap, Volume2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { useAuth } from "@/App";

export default function GuidedWalkthrough() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const trackId = params.get("walkthrough");

  const [track, setTrack] = useState(null);
  const [idx, setIdx] = useState(0);
  const [box, setBox] = useState(null);
  const [audio, setAudio] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);

  useEffect(() => {
    if (!trackId || !token) { setTrack(null); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/training/tracks/${trackId}`,
          { headers: { Authorization: `Bearer ${token}` } });
        const steps = (r.data.track.steps || []).filter((s) => s.route && s.testid);
        setTrack({ ...r.data.track, steps });
        setIdx(0);
      } catch { toast.error("Could not start that walkthrough"); }
    })();
  }, [trackId, token]);

  const current = track?.steps?.[idx];

  // Follow the step to its screen, then measure the target element.
  useEffect(() => {
    if (!current) return;
    if (current.route && !location.pathname.startsWith(current.route)) {
      navigate(`${current.route}?walkthrough=${trackId}`);
      return;
    }
    let tries = 0;
    const find = () => {
      const el = document.querySelector(`[data-testid="${current.testid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          const r = el.getBoundingClientRect();
          setBox({ top: r.top, left: r.left, width: r.width, height: r.height });
        }, 420);
        return;
      }
      if (tries++ < 12) setTimeout(find, 400);
      else setBox(null);
    };
    find();
  }, [current, location.pathname, navigate, trackId]);

  const record = useCallback(async (stepId, done = false) => {
    try {
      await axios.post(`${API}/training/progress`, {
        track_id: trackId, step_id: stepId, kind: "walkthrough", completed_track: done,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* progress is nice-to-have, never blocks training */ }
  }, [trackId, token]);

  const speak = async () => {
    if (!current) return;
    setLoadingAudio(true);
    try {
      const r = await axios.get(`${API}/training/narration/${trackId}/${current.id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      audio?.pause();
      const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      setAudio(a);
      a.play();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not play the narration");
    } finally { setLoadingAudio(false); }
  };

  const close = () => {
    audio?.pause();
    const next = new URLSearchParams(params);
    next.delete("walkthrough");
    setParams(next, { replace: true });
    setTrack(null);
  };

  const advance = async (delta) => {
    audio?.pause();
    if (current) await record(current.id);
    const next = idx + delta;
    if (next >= (track?.steps?.length || 0)) {
      await record(current?.id, true);
      toast.success("Walkthrough complete — it is on your training record");
      close();
      return;
    }
    setIdx(Math.max(0, next));
  };

  if (!track || !current) return null;

  const pad = 8;
  const tipTop = box ? Math.min(box.top + box.height + 14, window.innerHeight - 230) : 120;
  const tipLeft = box ? Math.min(Math.max(box.left, 16), Math.max(window.innerWidth - 400, 16)) : 24;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="guided-walkthrough">
      {box ? (
        <div className="absolute rounded-lg ring-4 ring-sky-400 shadow-[0_0_0_9999px_rgba(2,6,23,0.72)] transition-all duration-300"
          style={{ top: box.top - pad, left: box.left - pad,
            width: box.width + pad * 2, height: box.height + pad * 2 }}
          data-testid="walkthrough-spotlight" />
      ) : (
        <>
          <div className="absolute inset-0 bg-[#f6f9fc]/70" />
          <div className="absolute opacity-0 pointer-events-none" data-testid="walkthrough-spotlight" />
        </>
      )}

      <div className="absolute w-[min(380px,calc(100vw-32px))] rounded-xl bg-white ring-1 ring-sky-500/50 shadow-lg shadow-slate-200/70 p-4 pointer-events-auto"
        style={{ top: tipTop, left: tipLeft }} data-testid="walkthrough-tip">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-sky-600 inline-flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5" />
            {track.name} · {idx + 1}/{track.steps.length}
          </p>
          <button onClick={close} className="text-slate-600 hover:text-white" data-testid="walkthrough-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h4 className="text-sm font-black text-white mt-1.5">{current.title}</h4>
        <p className="text-[12.5px] text-slate-700 mt-1 leading-relaxed">{current.caption}</p>
        {!box && (
          <p className="text-[11px] text-amber-700 mt-2">
            This step's control is not on screen right now — it appears once you have a record open.
          </p>
        )}
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => advance(-1)}
            disabled={idx === 0} data-testid="walkthrough-back">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700" onClick={() => advance(1)}
            data-testid="walkthrough-next">
            {idx + 1 === track.steps.length ? "Finish" : "Next"}
            <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-700" onClick={speak}
            disabled={loadingAudio} data-testid="walkthrough-speak">
            {loadingAudio ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
          </Button>
          <button onClick={close} className="ml-auto text-[11px] text-slate-600 hover:underline"
            data-testid="walkthrough-skip">
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
